import * as Phaser from "phaser"
import { GAME_HEIGHT, GAME_WIDTH } from "../../data/constants"
import type { EnemyData, GameSprite } from "../../types"
import {
  type EnemyState,
  type EnemyStateContext,
  StatefulEnemyRuntime,
} from "../enemy-system"

const BOMBER_STAGING_X = GAME_WIDTH * 0.72
const BOMBER_APPROACH_SPEED = 2.7
const BOMBER_DIVE_SPEED = 5.9
const BOMBER_CLIMB_SPEED = 3.3
const BOMBER_AIM_MS = 520
const BOMBER_DIVE_MS = 780

type BomberPhase = "approach" | "aiming" | "dive" | "climb"

export class BomberEnemy extends StatefulEnemyRuntime<BomberPhase> {
  public constructor(sprite: GameSprite, enemyData: EnemyData) {
    const states: EnemyState<BomberPhase>[] = [
      {
        name: "approach",
        behaviorState: "tracking",
        animationState: "flying",
        canFire: false,
        update: (context) => this.updateApproach(context),
      },
      {
        name: "aiming",
        behaviorState: "focusing",
        animationState: "focusing",
        canFire: false,
        enter: (context) => {
          context.sprite.setTint(0xfbbf24)
          context.vfx.emitImpactBurst(
            context.sprite.x,
            context.sprite.y,
            0xfbbf24,
            0.8,
          )
        },
        update: (context) => this.updateAiming(context),
      },
      {
        name: "dive",
        behaviorState: "attacking",
        animationState: "attacking",
        canFire: true,
        enter: (context) => {
          context.sprite.clearTint()
          context.enemyData.lastShot = context.enemyData.shootCooldown
        },
        update: (context) => this.updateDive(context),
      },
      {
        name: "climb",
        behaviorState: "retreating",
        animationState: "retreating",
        canFire: false,
        update: (context) => this.updateClimb(context),
      },
    ]

    super(sprite, enemyData, "approach", states)
  }

  private updateApproach(
    context: EnemyStateContext<BomberPhase>,
  ): BomberPhase | null {
    context.enemyData.vx = context.scrollCompensation - BOMBER_APPROACH_SPEED
    context.enemyData.vy = Math.sin(context.time / 120) * 1.1

    if (context.sprite.x <= BOMBER_STAGING_X) {
      return "aiming"
    }

    return null
  }

  private updateAiming(
    context: EnemyStateContext<BomberPhase>,
  ): BomberPhase | null {
    context.enemyData.vx = context.scrollCompensation - 0.35
    context.enemyData.vy = Phaser.Math.Clamp(
      (context.playerY - context.sprite.y) * 0.055,
      -2,
      2,
    )
    context.sprite.angle = Math.sin(context.stateTime / 45) * 7

    if (context.stateTime >= BOMBER_AIM_MS) {
      return "dive"
    }

    return null
  }

  private updateDive(
    context: EnemyStateContext<BomberPhase>,
  ): BomberPhase | null {
    context.enemyData.vx = context.scrollCompensation - BOMBER_DIVE_SPEED
    context.enemyData.vy = Phaser.Math.Clamp(
      (context.playerY + 24 - context.sprite.y) * 0.045,
      -1.4,
      3.1,
    )
    context.sprite.angle = Phaser.Math.Clamp(context.enemyData.vy * 10, -12, 24)

    if (context.stateTime >= BOMBER_DIVE_MS || context.sprite.x < 80) {
      return "climb"
    }

    return null
  }

  private updateClimb(
    context: EnemyStateContext<BomberPhase>,
  ): BomberPhase | null {
    context.enemyData.vx = context.scrollCompensation + BOMBER_CLIMB_SPEED
    context.enemyData.vy = (GAME_HEIGHT * 0.28 - context.sprite.y) * 0.045
    context.sprite.angle = -10

    if (context.sprite.x > GAME_WIDTH + 90) {
      context.sprite.destroy()
    }

    return null
  }
}
